// The editor's controller (decision 034): the list of blocks and the operations on it that keys, the mouse and the menus call.
// Kept outside React state handlers so every handler sees the current list, not the one of its render.

import { escInline, inlineText, newBlock, serializeBlocks, type Block, type BlockKind } from '@confdeck/core-shared';
import { setCaret } from './caret.ts';

export interface Slash { id: string; query: string; from: number; active: number; x: number; y: number }
type At = number | 'end';

export class EditorCtl {
  blocks: Block[];
  emitted: string;
  /** Texts this editor sent lately: the server echoes them back, and an echo of an older one is not someone else's edit. */
  sent: string[] = [];
  focusReq: { id: string; at: At } | null = null;
  /** Files on their way to the server: the address in the text → a local preview shown until the bytes are there. */
  previews = new Map<string, string>();
  slash: Slash | null = null;
  root: HTMLElement | null = null;
  /** Re-renders the editor with the current list; `now` — the change is saved without waiting (a file is about to be sent). */
  render: (now?: boolean) => void = () => {};
  onChange: (md: string, now?: boolean) => void = () => {};
  /** The kinds this text may hold (a letter, a block of a site); null — all of them. */
  kinds: ReadonlySet<BlockKind> | null = null;
  /** Asks for a file or a record for a block: the editor opens its picker. */
  ask: (what: 'file' | 'record' | 'menu' | null, id?: string) => void = () => {};

  constructor(blocks: Block[]) {
    this.blocks = blocks.length ? blocks : [newBlock('p')];
    this.emitted = serializeBlocks(this.blocks);
  }

  allows = (k: BlockKind) => !this.kinds || this.kinds.has(k);
  /** Pasted blocks of a kind this text does not hold become paragraphs of their text; a line, a file and a record are left out. */
  fit = (bs: Block[]): Block[] => bs.flatMap((b) => (this.allows(b.kind) ? [b] : b.kind === 'divider' || b.kind === 'file' || b.kind === 'record' ? [] : [newBlock('p', b.kind === 'code' ? escInline(b.text) : b.text)]));

  index = (id: string) => this.blocks.findIndex((b) => b.id === id);
  get = (id: string) => this.blocks.find((b) => b.id === id);
  el = (id: string) => this.root?.querySelector<HTMLElement>(`[data-bid="${id}"] [data-edit]`) ?? null;

  /** Puts a new list in place, tells the owner the text when it changed and re-renders. */
  commit(next: Block[], now = false): void {
    this.blocks = next.length ? next : [newBlock('p')];
    const md = serializeBlocks(this.blocks);
    if (md !== this.emitted) { this.emitted = md; this.sent = [md, ...this.sent].slice(0, 30); this.onChange(md, now); }
    this.render(now);
  }

  /** Whether a text that came in is someone else's: not what this editor shows, not an echo of what it sent. */
  foreign(md: string): boolean { return md !== this.emitted && !this.sent.includes(md); }

  /** Takes a text that came from elsewhere (another person, a restored version) while nobody types here. */
  reset(blocks: Block[]): void {
    this.blocks = blocks.length ? blocks : [newBlock('p')];
    this.emitted = serializeBlocks(this.blocks);
    this.render();
  }

  focus(id: string, at: At = 'end'): void {
    this.focusReq = { id, at };
    const el = this.el(id);
    if (el) setCaret(el, at);
  }

  patch(id: string, p: Partial<Block>, now = false): void { this.commit(this.blocks.map((b) => (b.id === id ? { ...b, ...p } : b)), now); }
  setText(id: string, text: string): void { if (this.get(id)?.text !== text) this.patch(id, { text }); }

  insertAfter(id: string | null, add: Block[], focus: At | null = 'end'): void {
    const i = id === null ? -1 : this.index(id);
    this.commit([...this.blocks.slice(0, i + 1), ...add, ...this.blocks.slice(i + 1)]);
    const last = add[add.length - 1];
    if (last && focus !== null) this.focus(last.id, focus);
  }

  insertBefore(id: string, add: Block): void {
    const i = this.index(id);
    this.commit([...this.blocks.slice(0, i), add, ...this.blocks.slice(i)]);
  }

  remove(id: string, focusNear = true): void {
    const i = this.index(id);
    const near = this.blocks[i - 1] ?? this.blocks[i + 1];
    this.commit(this.blocks.filter((b) => b.id !== id));
    if (focusNear && near) this.focus(near.id, i > 0 ? 'end' : 0);
    else if (focusNear) this.focus(this.blocks[0]!.id, 0);
  }

  /** Moves a block to a place in the list (drag and drop, «Выше»/«Ниже», Ctrl+Shift+стрелки). */
  moveTo(id: string, target: string, pos: 'before' | 'after'): void {
    if (id === target) return;
    const b = this.get(id);
    if (!b) return;
    const rest = this.blocks.filter((x) => x.id !== id);
    const t = rest.findIndex((x) => x.id === target);
    if (t < 0) return;
    const at = pos === 'before' ? t : t + 1;
    this.commit([...rest.slice(0, at), b, ...rest.slice(at)]);
  }
  moveBy(id: string, delta: -1 | 1): void {
    const i = this.index(id);
    const other = this.blocks[i + delta];
    if (other) { this.moveTo(id, other.id, delta < 0 ? 'before' : 'after'); this.focus(id, 'end'); }
  }

  /**
   * Enter in a text block, the caret dividing its text into `before` and `after`. At the start of a filled block a line opens above
   * and the block stays what it is; otherwise the rest goes to a new block below — a list item continues the list.
   */
  split(id: string, before: string, after: string): void {
    const b = this.get(id);
    if (!b) return;
    if (!before.trim() && after.trim()) { this.setText(id, after); this.insertBefore(id, newBlock('p')); this.focus(id, 0); return; }
    const list = b.kind === 'bullet' || b.kind === 'number' || b.kind === 'todo';
    this.setText(id, before);
    this.insertAfter(id, [newBlock(list ? b.kind : 'p', after, b.kind === 'todo' ? { checked: false } : {})], 0);
  }

  /**
   * Backspace at the start of a paragraph: its text joins the block above, as in Notion. A divider, a file or a record above is
   * taken as one piece: the first press selects it, the next one removes it (a picture is not lost by one key).
   * `at` — where the caret goes in the block above (the length of its visible text).
   */
  mergeUp(id: string, text: string, at: number): void {
    const i = this.index(id);
    const prev = this.blocks[i - 1];
    if (!prev) return;
    const piece = prev.kind === 'divider' || prev.kind === 'file' || prev.kind === 'record';
    // An empty line goes away; a filled one stays beside a piece or a code block, whose text is not inline marks.
    if (!text.trim() && (piece || prev.kind === 'code')) { this.commit(this.blocks.filter((x) => x.id !== id)); this.focus(prev.id, piece ? 0 : 'end'); return; }
    if (piece || prev.kind === 'code') { this.setText(id, text); this.focus(prev.id, piece ? 0 : 'end'); return; }
    this.commit(this.blocks.filter((x) => x.id !== id).map((x) => (x.id === prev.id ? { ...x, text: x.text + text } : x)));
    this.focus(prev.id, at);
  }

  /** Delete at the end of a text block: the next text block joins it. */
  mergeDown(id: string, text: string, at: number): void {
    const next = this.blocks[this.index(id) + 1];
    if (!next || next.kind === 'code' || next.kind === 'divider' || next.kind === 'file' || next.kind === 'record') return;
    this.commit(this.blocks.filter((x) => x.id !== next.id).map((x) => (x.id === id ? { ...x, text: text + next.text } : x)));
    this.focus(id, at);
  }

  /** Pasted lines become blocks at the caret: an empty-before paragraph is replaced by them, the text after the caret follows them. */
  pasteBlocks(id: string, before: string, after: string, add: Block[]): void {
    const b = this.get(id);
    if (!b || !add.length) return;
    const tail = after.trim() ? [newBlock('p', after)] : [];
    if (!before.trim() && b.kind === 'p') this.commit(this.blocks.flatMap((x) => (x.id === id ? [...add, ...tail] : [x])));
    else this.commit(this.blocks.flatMap((x) => (x.id === id ? [{ ...x, text: before }, ...add, ...tail] : [x])));
    this.focus(add[add.length - 1]!.id, 'end');
  }

  duplicate(id: string): void {
    const b = this.get(id);
    if (b) this.insertAfter(id, [{ ...b, id: newBlock().id }]);
  }

  /** «Превратить в»: the text stays; code keeps the plain text of marks, a paragraph made of code escapes it. */
  turnInto(id: string, kind: BlockKind): void {
    const b = this.get(id);
    if (!b || b.kind === kind) return;
    if (kind === 'file' || kind === 'record') { this.ask(kind, id); return; }
    if (kind === 'divider') { this.commit(this.blocks.map((x) => (x.id === id ? newBlock('divider') : x))); return; }
    const text = kind === 'code' ? inlineText(b.text) : b.kind === 'code' ? escInline(b.text) : b.text;
    this.patch(id, { kind, text, ...(kind === 'todo' ? { checked: false } : {}) });
    this.focus(id, 'end');
  }
}
